const clpFormatter = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});

export function formatCLP(monto: number): string {
  return clpFormatter.format(monto);
}

/**
 * Formato compacto para tick labels de charts (ejes Y).
 * Convierte 1.500.000 → "1,5M" y 35.000 → "35k" usando notación
 * chilena (coma decimal). Para valores < 1.000 devuelve el número
 * tal cual sin separador de miles (suelen ser cantidades chicas
 * donde el espacio del eje no permite más caracteres).
 *
 * Sprint 17 S17-Z9: antes los charts usaban `toFixed(1)` directo,
 * produciendo "1.5M" con punto decimal (notación inglesa).
 */
export function formatTickCompacto(v: number): string {
  if (v >= 1_000_000) {
    return `${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
  }
  if (v >= 1_000) {
    return `${Math.round(v / 1_000)}k`;
  }
  return `${v}`;
}

/**
 * Formatea un celular chileno canónico (9 dígitos, empieza con 9) al
 * formato visual estándar `+56 9 XXXX XXXX`.
 *
 * Sprint 14 S14-Cel. Doctrina del celular: la BD almacena solo los 9
 * dígitos (`912345678`); el `+56` es prefijo visual fijo de Chile que
 * NO se guarda. El espacio entre el "9" prefijo móvil y los dos grupos
 * de 4 reproduce el formato impreso usual.
 *
 * Si `raw` no calza con el formato canónico (no es 9 dígitos, no empieza
 * con 9), devuelve el valor crudo sin modificar — patrón defensivo igual
 * que `formatPatente` y `maskValue`.
 */
export function formatCelular(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 9 && digits.startsWith('9')) {
    return `+56 9 ${digits.slice(1, 5)} ${digits.slice(5, 9)}`;
  }
  return raw;
}

/**
 * Formatea un RUT chileno canónico (cuerpo numérico + guión + DV) al
 * formato visual con puntos separadores de miles.
 *
 * Sprint 15 S15-01. Doctrina del RUT: la BD almacena el formato canónico
 * sin puntos (`12345678-5`); el display agrega puntos (`12.345.678-5`).
 * Mismo patrón que `formatCelular` (storage canónico, display con
 * formato visual).
 *
 * Si `raw` no calza con el patrón canónico (no tiene guión, cuerpo no
 * numérico), devuelve el valor crudo sin modificar.
 */
export function formatRut(raw: string | null | undefined): string {
  if (!raw) return '';
  const idx = raw.lastIndexOf('-');
  if (idx === -1) return raw;
  const cuerpo = raw.slice(0, idx);
  const dv = raw.slice(idx + 1);
  if (!/^\d+$/.test(cuerpo)) return raw;
  const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${conPuntos}-${dv}`;
}

/**
 * Valida un RUT chileno con módulo 11 y devuelve un objeto con el
 * resultado y el RUT normalizado al formato canónico `XXXXXXXX-Y`
 * (sin puntos, con guión, DV K en mayúscula).
 *
 * Sprint 15 S15-Vec.3. Replica la doctrina del backend
 * (`normalizar_rut_chileno` en `apps/backend/app/schemas/validators.py`)
 * en el cliente para dar feedback inmediato al usuario antes del submit.
 *
 * Acepta inputs con puntos, guión, espacios y DV en mayúscula o minúscula.
 * Retorna `{ valid: false, canonico: null }` cuando:
 * - El input está vacío o solo tiene whitespace.
 * - El cuerpo no es numérico o está fuera del rango (1..99_999_999).
 * - El DV no coincide con el resultado del módulo 11.
 */
export function validarRutChileno(raw: string | null | undefined): {
  valid: boolean;
  canonico: string | null;
} {
  if (!raw) return { valid: false, canonico: null };
  const limpio = raw.replace(/\./g, '').replace(/\s/g, '').toUpperCase();
  const m = /^(\d{1,8})-([0-9K])$/.exec(limpio);
  if (!m) return { valid: false, canonico: null };
  const cuerpo = parseInt(m[1], 10);
  const dvEntrada = m[2];
  if (cuerpo <= 0 || cuerpo > 99_999_999) {
    return { valid: false, canonico: null };
  }
  // Cálculo mod11 — mismo algoritmo que el backend.
  const multiplicadores = [2, 3, 4, 5, 6, 7];
  let total = 0;
  const cuerpoStr = String(cuerpo);
  for (let i = 0; i < cuerpoStr.length; i++) {
    const digito = parseInt(cuerpoStr[cuerpoStr.length - 1 - i], 10);
    total += digito * multiplicadores[i % 6];
  }
  const resto = total % 11;
  const dvNum = 11 - resto;
  const dvEsperado = dvNum === 11 ? '0' : dvNum === 10 ? 'K' : String(dvNum);
  if (dvEsperado !== dvEntrada) {
    return { valid: false, canonico: null };
  }
  return { valid: true, canonico: `${cuerpo}-${dvEsperado}` };
}

/**
 * Formatea un RUT chileno mientras el usuario escribe. Se usa en
 * `onChange` de inputs para guiar al vecino al patrón `XX.XXX.XXX-K`
 * en tiempo real (Sprint 17 Z21+Z23).
 *
 * Acepta cualquier mezcla de dígitos, puntos, guión, K mayúscula o
 * minúscula. Devuelve un string parcialmente formateado mientras se
 * escribe (no requiere DV completo) y completamente formateado al
 * tener 2+ caracteres válidos.
 *
 * Ejemplos:
 *   "12345678K"      → "12.345.678-K"
 *   "12.345.678-K"   → "12.345.678-K"
 *   "1234"           → "123-4"
 *   "123"            → "12-3"
 *   ""               → ""
 *   "abc"            → ""
 */
export function formatearRutEnVivo(raw: string): string {
  const limpio = raw.replace(/[^0-9kK]/g, '').toUpperCase();
  if (limpio.length === 0) return '';
  if (limpio.length === 1) return limpio;
  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1);
  const cuerpoConPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${cuerpoConPuntos}-${dv}`;
}

/**
 * Formatea un celular chileno mientras el usuario escribe. Mantiene
 * solo los 9 dígitos canónicos (`9XXXXXXXX`) y los separa visualmente
 * como `9 XXXX XXXX`. El prefijo visual `+56 ` se renderiza aparte
 * (no se incluye en el value del input).
 *
 * Doctrina celular (Sprint 14): se almacena solo `9XXXXXXXX`, el +56
 * es prefijo visual fijo de UI.
 *
 * Ejemplos:
 *   "987654321"   → "9 8765 4321"
 *   "+569123"     → "9 123" (descarta + y 56 si vienen pegados al 9)
 *   "9 8765"      → "9 8765"
 */
export function formatearCelularEnVivo(raw: string): string {
  // Quitar +56 / 56 si vienen como prefijo, y todo lo que no sea dígito.
  let solo = raw.replace(/\D/g, '');
  if (solo.startsWith('56')) solo = solo.slice(2);
  if (solo.length > 9) solo = solo.slice(0, 9);
  if (solo.length === 0) return '';
  if (solo.length <= 1) return solo;
  if (solo.length <= 5) return `${solo[0]} ${solo.slice(1)}`;
  return `${solo[0]} ${solo.slice(1, 5)} ${solo.slice(5)}`;
}

/**
 * Devuelve los 9 digitos canonicos del celular a partir de un input
 * formateado o no. Devuelve null si el input no tiene exactamente 9
 * digitos empezando por 9.
 */
export function celularCanonico(raw: string): string | null {
  let solo = raw.replace(/\D/g, '');
  if (solo.startsWith('56')) solo = solo.slice(2);
  if (solo.length !== 9 || !solo.startsWith('9')) return null;
  return solo;
}

/**
 * Validez de formato de datos verificables, para chips de "heads-up" en la
 * UI (no validación dura: marcan datos sospechosos de un vistazo). Centralizan
 * el criterio para que las vistas que validan (cola de la Comisión, etc.) no
 * lo repitan inline. Sprint 19 S19-A4.
 */
export function rutOk(rut: string | null | undefined): boolean {
  return validarRutChileno(rut).valid;
}

export function emailOk(email: string | null | undefined): boolean {
  return !!email && /\S+@\S+\.\S+/.test(email);
}

export function celularOk(celular: string | null | undefined): boolean {
  return !!celular && celularCanonico(celular) !== null;
}

export function formatFecha(iso: string): string {
  // ISO con tiempo o solo YYYY-MM-DD. Para fechas sin tiempo, parsea local
  // para evitar shift de timezone (UTC midnight = día anterior en Chile).
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const date = dateOnly ? new Date(`${iso}T00:00:00`) : new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}
